import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PlusIcon, UsersIcon } from "@heroicons/react/24/outline";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { EmptyState } from "@/components/ui/EmptyState";
import { Spinner } from "@/components/ui/Spinner";
import { Table } from "@/components/ui/Table";
import { Dialog } from "@/components/ui/Dialog";
import { hasFormChanges } from "@/components/ui/dialogCloseGuard";
import { ApiError } from "@/lib/apiClient";
import { useCurrentUser, userIsBuero } from "@/lib/auth";
import {
  ansprechpartnerDisplayName,
  createKontaktAnsprechpartner,
  deleteKontaktAnsprechpartner,
  listGeschaeftspartnerAnsprechpartner,
  updateKontaktAnsprechpartner,
  type KontaktAnsprechpartner,
} from "@/lib/kontakte";
import { useRealtimeSync } from "@/lib/useRealtime";
import { AnsprechpartnerFormFields } from "@/pages/kontakte/AnsprechpartnerFormFields";
import {
  EMPTY_AP_FORM,
  apFormFromKontakt,
  apPayloadFromForm,
  applyApFormUpdate,
  ansprechpartnerFieldsMatch,
  mergeSavedAnsprechpartner,
  preserveSavedAnsprechpartner,
  validateApForm,
  type ApForm,
  type ApFormUpdater,
} from "@/pages/kontakte/ansprechpartnerFormState";
import { ROW_ACTIONS_HEADER_CLASS, TableRowActions } from "@/pages/kontakte/TableRowActions";

interface AnsprechpartnerSectionProps {
  geschaeftspartnerId: number;
  /** Ansprechpartner, die die Suche getroffen hat und die deshalb hervorgehoben werden. */
  highlightIds?: number[];
}

export function AnsprechpartnerSection({
  geschaeftspartnerId,
  highlightIds = [],
}: AnsprechpartnerSectionProps) {
  const { user } = useCurrentUser();
  const canEdit = userIsBuero(user);
  const [rows, setRows] = useState<KontaktAnsprechpartner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<ApForm>(EMPTY_AP_FORM);
  const [formBaseline, setFormBaseline] = useState<ApForm>(EMPTY_AP_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const requestSeq = useRef(0);
  const formRef = useRef(form);
  const editIdRef = useRef(editId);
  /** Gerade gespeicherte Zeilen, die ein veraltetes Nachladen nicht verwerfen darf. */
  const savedOverlay = useRef(new Map<number, { row: KontaktAnsprechpartner; until: number }>());
  formRef.current = form;
  editIdRef.current = editId;

  const applySavedOverlay = useCallback((list: KontaktAnsprechpartner[]) => {
    const pending = savedOverlay.current;
    if (pending.size === 0) return list;
    const now = Date.now();
    let next = list;
    for (const [id, entry] of pending) {
      if (now >= entry.until) {
        pending.delete(id);
        continue;
      }
      const confirmed = next.find((row) => row.id === id);
      if (confirmed && ansprechpartnerFieldsMatch(confirmed, entry.row)) {
        pending.delete(id);
        continue;
      }
      next = preserveSavedAnsprechpartner(next, entry.row);
    }
    return next;
  }, []);

  const reload = useCallback(async (options?: { silent?: boolean }) => {
    const seq = ++requestSeq.current;
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const list = await listGeschaeftspartnerAnsprechpartner(geschaeftspartnerId);
      if (seq !== requestSeq.current) return list;
      setRows(applySavedOverlay(list));
      return list;
    } catch (err) {
      if (seq !== requestSeq.current) return [];
      setError(
        err instanceof ApiError
          ? err.message
          : "Ansprechpartner konnten nicht geladen werden.",
      );
      if (!options?.silent) setRows([]);
      return [];
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [applySavedOverlay, geschaeftspartnerId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useRealtimeSync({
    domains: ["kontakte"],
    paused: formOpen,
    onChange: () => void reload({ silent: true }),
  });

  function openCreate() {
    setEditId(null);
    setForm(EMPTY_AP_FORM);
    setFormBaseline(EMPTY_AP_FORM);
    setFormError(null);
    setFormOpen(true);
  }

  function openEdit(ap: KontaktAnsprechpartner) {
    const initial = apFormFromKontakt(ap);
    setEditId(ap.id);
    setForm(initial);
    setFormBaseline(initial);
    setFormError(null);
    setFormOpen(true);
  }

  function updateForm(updater: ApFormUpdater) {
    setForm((current) => applyApFormUpdate(current, updater));
  }

  async function handleSave() {
    const currentForm = formRef.current;
    const currentEditId = editIdRef.current;
    const invalid = validateApForm(currentForm);
    if (invalid) {
      setFormError(invalid);
      return;
    }
    const payload = apPayloadFromForm(currentForm, geschaeftspartnerId);
    // Laufende Listen-Reloads stammen von vor dem Speichern und dürfen die
    // gerade geschriebenen Felder nicht wieder überschreiben.
    ++requestSeq.current;
    setSaving(true);
    setFormError(null);
    try {
      const saved = currentEditId
        ? await updateKontaktAnsprechpartner(currentEditId, payload)
        : await createKontaktAnsprechpartner(payload);
      savedOverlay.current.set(saved.id, { row: saved, until: Date.now() + 8000 });
      setRows((current) => mergeSavedAnsprechpartner(current, saved));
      setFormOpen(false);
      await reload({ silent: true });
    } catch (err) {
      setFormError(
        err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(ap: KontaktAnsprechpartner) {
    if (!window.confirm(`Ansprechpartner „${ansprechpartnerDisplayName(ap)}“ wirklich löschen?`)) {
      return;
    }
    try {
      await deleteKontaktAnsprechpartner(ap.id);
      await reload({ silent: true });
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Ansprechpartner konnte nicht gelöscht werden.",
      );
    }
  }

  const highlightSet = useMemo(() => new Set(highlightIds), [highlightIds]);
  const actionsRef = useRef({
    openEdit,
    handleDelete,
  });
  actionsRef.current = { openEdit, handleDelete };

  useEffect(() => {
    if (loading || highlightSet.size === 0) return;
    const first = rows.find((row) => highlightSet.has(row.id));
    if (!first) return;
    document.getElementById(`ansprechpartner-${first.id}`)?.scrollIntoView({
      block: "center",
      behavior: "smooth",
    });
  }, [highlightSet, loading, rows]);

  const columns = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        sortable: true,
        sortValue: (ap: KontaktAnsprechpartner) => ansprechpartnerDisplayName(ap),
        headerClassName: "w-[30%]",
        render: (ap: KontaktAnsprechpartner) => (
          <div id={`ansprechpartner-${ap.id}`} className="flex min-w-0 items-center gap-3">
            <span
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-fill-tertiary text-xs font-semibold text-label"
              aria-hidden
            >
              {initialsFromPerson(ap)}
            </span>
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-2">
                <span className="truncate font-medium text-label">{ansprechpartnerDisplayName(ap)}</span>
                {highlightSet.has(ap.id) ? (
                  <span className="shrink-0 rounded-full bg-accent/15 px-2 py-0.5 text-[11px] font-semibold text-accent">
                    Suchtreffer
                  </span>
                ) : null}
              </div>
              {ap.funktion?.trim() ? (
                <div className="truncate text-xs text-label-secondary">{ap.funktion.trim()}</div>
              ) : null}
            </div>
          </div>
        ),
      },
      {
        key: "telefon",
        header: "Telefon",
        sortable: true,
        sortValue: (ap: KontaktAnsprechpartner) => ap.telefonnummer || "",
        headerClassName: "w-[150px]",
        className: "whitespace-nowrap",
        render: (ap: KontaktAnsprechpartner) => <TelLink value={ap.telefonnummer} />,
      },
      {
        key: "mobil",
        header: "Mobil",
        sortable: true,
        sortValue: (ap: KontaktAnsprechpartner) => ap.mobiltelefon || "",
        headerClassName: "w-[150px]",
        className: "whitespace-nowrap",
        render: (ap: KontaktAnsprechpartner) => <TelLink value={ap.mobiltelefon} />,
      },
      {
        key: "mail",
        header: "E-Mail",
        sortable: true,
        sortValue: (ap: KontaktAnsprechpartner) => ap.mail || "",
        className: "min-w-0",
        render: (ap: KontaktAnsprechpartner) => (
          <div className="min-w-0">
            <MailLink value={ap.mail} />
            {ap.team_mail?.trim() ? (
              <div className="truncate text-xs text-label-secondary" title={ap.team_mail.trim()}>
                Team: {ap.team_mail.trim()}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        key: "actions",
        header: "",
        headerClassName: ROW_ACTIONS_HEADER_CLASS.two,
        render: (ap: KontaktAnsprechpartner) => (
          <TableRowActions
            onEdit={() => actionsRef.current.openEdit(ap)}
            onDelete={() => void actionsRef.current.handleDelete(ap)}
          />
        ),
      },
    ],
    [highlightSet],
  );

  return (
    <Card
      title="Ansprechpartner"
      description={
        rows.length > 0
          ? `${rows.length.toLocaleString("de-DE")} ${rows.length === 1 ? "Person" : "Personen"} – in Projekten, Angeboten und Rechnungen auswählbar.`
          : "Personen, die in Projekten, Angeboten und Rechnungen ausgewählt werden können."
      }
      actions={
        canEdit ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={openCreate}
            leftIcon={<PlusIcon className="h-4 w-4" aria-hidden />}
          >
            Ansprechpartner anlegen
          </Button>
        ) : null
      }
    >
      {error ? (
        <Alert variant="error" title="Fehler" className="mb-4">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<UsersIcon />}
          title="Noch keine Ansprechpartner"
          description="Lege die erste Person an – Name, Funktion und Kontaktdaten reichen aus."
          action={
            canEdit ? (
              <Button size="sm" onClick={openCreate} leftIcon={<PlusIcon className="h-4 w-4" aria-hidden />}>
                Ansprechpartner anlegen
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Table
          columns={canEdit ? columns : columns.filter((column) => column.key !== "actions")}
          rows={rows}
          rowKey={(ap) => ap.id}
          rowClassName={(ap) =>
            highlightSet.has(ap.id) ? "align-middle bg-accent/10 ring-1 ring-inset ring-accent" : "align-middle"
          }
          stickyEndColumnCount={canEdit ? 1 : 0}
        />
      )}

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editId ? "Ansprechpartner bearbeiten" : "Ansprechpartner anlegen"}
        size="lg"
        dirty={hasFormChanges(form, formBaseline)}
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setFormOpen(false)} disabled={saving}>
              Abbrechen
            </Button>
            <Button type="button" onClick={() => void handleSave()} disabled={saving}>
              {saving ? "Speichern…" : "Speichern"}
            </Button>
          </>
        }
      >
        {formError ? (
          <Alert variant="error" title="Eingabe" className="mb-4">
            {formError}
          </Alert>
        ) : null}
        <AnsprechpartnerFormFields key={editId ?? "neu"} form={form} onChange={updateForm} />
      </Dialog>
    </Card>
  );
}

function Muted() {
  return <span className="text-label-tertiary">—</span>;
}

function initialsFromPerson(ap: KontaktAnsprechpartner): string {
  const v = (ap.vorname || "").trim();
  const n = (ap.nachname || "").trim();
  return `${v[0] ?? ""}${n[0] ?? ""}`.toUpperCase() || "?";
}

function TelLink({ value }: { value?: string | null }) {
  const tel = (value || "").trim();
  if (!tel) return <Muted />;
  return (
    <a href={`tel:${tel}`} className="tabular-nums text-label hover:text-accent hover:underline">
      {tel}
    </a>
  );
}

function MailLink({ value }: { value?: string | null }) {
  const mail = (value || "").trim();
  if (!mail) return <Muted />;
  return (
    <a
      href={`mailto:${mail}`}
      title={mail}
      className="block truncate text-label hover:text-accent hover:underline"
    >
      {mail}
    </a>
  );
}
